import React from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Paper,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Save,
  PersonOutline,
  Visibility,
  VisibilityOff,
  WhatsApp,
  Phone,
  MailOutline
} from '@mui/icons-material';
import { User } from '../../../types';
import { ZERDE_PRIMARY } from '../types';
import { handlePhoneFocus, handlePhoneBlur } from '../../UserTableRow';

interface UserProfileSectionProps {
  user: User;
  displayName: string;
  roleName: string;
  locationBadge: string;
  currentRegionName: string;
  mailAddress: string | null;
  isEditingContact: boolean;
  setIsEditingContact: (v: boolean) => void;
  fullName: string;
  setFullName: (v: string) => void;
  userphone: string;
  setUserphone: (v: string) => void;
  workPhone: string;
  setWorkPhone: (v: string) => void;
  iin: string;
  setIin: (v: string) => void;
  showIIN: boolean;
  setShowIIN: (v: boolean) => void;
  savingPersonal: boolean;
  handleSavePersonal: (e?: React.FormEvent) => void;
  handlePhoneInputChange: (value: string, setter: (v: string) => void) => void;
  canEditUser: boolean;
  canViewIIN: boolean;
  displayUserphone: string | null;
  displayWorkPhone: string | null;
  isUserphoneWA: boolean;
  isWorkPhoneWA: boolean;
  myPharmacy: string;
  currentAddress: string;
  loadingGeo: boolean;
}

export const UserProfileSection: React.FC<UserProfileSectionProps> = ({
  user,
  displayName: _displayName,
  roleName,
  locationBadge,
  currentRegionName,
  mailAddress,
  isEditingContact,
  setIsEditingContact,
  fullName,
  setFullName,
  userphone,
  setUserphone,
  workPhone,
  setWorkPhone,
  iin,
  setIin,
  showIIN,
  setShowIIN,
  savingPersonal,
  handleSavePersonal,
  handlePhoneInputChange,
  canEditUser,
  canViewIIN,
  displayUserphone,
  displayWorkPhone,
  isUserphoneWA,
  isWorkPhoneWA,
  myPharmacy,
  currentAddress,
  loadingGeo
}) => {
  // Разбор ФИО для Битрикс24 (Имя, Фамилия, Отчество)
  const nameParts = (fullName || user.username).trim().split(/\s+/);
  const lastName = nameParts.length > 1 ? nameParts[0] : '';
  const firstName = nameParts.length > 1 ? nameParts[1] : nameParts[0];
  const middleName = nameParts.length > 2 ? nameParts.slice(2).join(' ') : '';

  return (
    <Box id="user-section-profile" sx={{ scrollMarginTop: '60px', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Карточка 1: Контактная информация */}
      <Paper
        elevation={0}
        sx={{
          p: 3.5,
          bgcolor: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, pb: 1.5, borderBottom: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonOutline sx={{ color: ZERDE_PRIMARY, fontSize: 22 }} />
            <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700, fontSize: '1.15rem' }}>
              Контактная информация
            </Typography>
          </Box>

          {canEditUser && (
            !isEditingContact ? (
              <Button
                size="small"
                onClick={() => setIsEditingContact(true)}
                startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                sx={{
                  color: ZERDE_PRIMARY,
                  textTransform: 'lowercase',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' }
                }}
              >
                изменить
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSavePersonal}
                  disabled={savingPersonal}
                  startIcon={savingPersonal ? <CircularProgress size={14} /> : <Save sx={{ fontSize: 16 }} />}
                  sx={{
                    bgcolor: ZERDE_PRIMARY,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '8px',
                    '&:hover': { bgcolor: '#105ec2' }
                  }}
                >
                  Сохранить
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setIsEditingContact(false);
                  }}
                  sx={{
                    textTransform: 'none',
                    color: '#64748b',
                    borderColor: '#cbd5e1',
                    borderRadius: '8px'
                  }}
                >
                  Отмена
                </Button>
              </Box>
            )
          )}
        </Box>

        {/* РЕЖИМ ПРОСМОТРА */}
        {!isEditingContact ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.3 }}>
                  ИМЯ
                </Typography>
                <Typography variant="body1" sx={{ color: '#0f172a', fontWeight: 600 }}>
                  {firstName || user.username}
                </Typography>
              </Box>

              {lastName && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.3 }}>
                    ФАМИЛИЯ
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#0f172a', fontWeight: 600 }}>
                    {lastName}
                  </Typography>
                </Box>
              )}
            </Box>

            {middleName && (
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.3 }}>
                  ОТЧЕСТВО
                </Typography>
                <Typography variant="body1" sx={{ color: '#0f172a', fontWeight: 600 }}>
                  {middleName}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.3 }}>
                  РОЛЬ
                </Typography>
                <Typography variant="body1" sx={{ color: '#0f172a', fontWeight: 600 }}>
                  {roleName}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.3 }}>
                  ФИЛИАЛ
                </Typography>
                <Typography variant="body1" sx={{ color: '#0f172a', fontWeight: 600 }}>
                  {locationBadge}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.3 }}>
                  РЕГИОН
                </Typography>
                <Typography variant="body1" sx={{ color: '#0f172a', fontWeight: 600 }}>
                  {currentRegionName}
                </Typography>
              </Box>

              {/* Без users.view_iin сервер не отдает ИИН - поле не показываем */}
              {canViewIIN && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.3 }}>
                    ИИН (12 ЦИФР)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ color: '#0f172a', fontWeight: 600, fontFamily: 'monospace', letterSpacing: 0.5 }}>
                      {showIIN ? (iin || '-') : (iin ? '••••••••••••' : '-')}
                    </Typography>
                    {iin && (
                      <IconButton size="small" onClick={() => setShowIIN(!showIIN)}>
                        {showIIN ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    )}
                  </Box>
                </Box>
              )}
            </Box>

            {/* ТЕЛЕФОНЫ */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.3 }}>
                  МОБИЛЬНЫЙ ТЕЛЕФОН (+7)
                </Typography>
                {displayUserphone ? (
                  isUserphoneWA ? (
                    <Box
                      component="a"
                      href={`https://wa.me/${userphone.replace(/\D/g, '').replace(/^8/, '7')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.6,
                        color: '#16a34a',
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      <WhatsApp sx={{ fontSize: 16 }} />
                      {displayUserphone}
                    </Box>
                  ) : (
                    <Box
                      component="a"
                      href={`tel:${displayUserphone}`}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.6,
                        color: '#1e293b',
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      <Phone sx={{ fontSize: 16, color: '#64748b' }} />
                      {displayUserphone}
                    </Box>
                  )
                ) : (
                  <Typography variant="body1" sx={{ color: '#94a3b8' }}>-</Typography>
                )}
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.3 }}>
                  РАБОЧИЙ ТЕЛЕФОН (+7)
                </Typography>
                {displayWorkPhone ? (
                  isWorkPhoneWA ? (
                    <Box
                      component="a"
                      href={`https://wa.me/${workPhone.replace(/\D/g, '').replace(/^8/, '7')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.6,
                        color: '#16a34a',
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      <WhatsApp sx={{ fontSize: 16 }} />
                      {displayWorkPhone}
                    </Box>
                  ) : (
                    <Box
                      component="a"
                      href={`tel:${displayWorkPhone}`}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.6,
                        color: '#1e293b',
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      <Phone sx={{ fontSize: 16, color: '#64748b' }} />
                      {displayWorkPhone}
                    </Box>
                  )
                ) : (
                  <Typography variant="body1" sx={{ color: '#94a3b8' }}>-</Typography>
                )}
              </Box>
            </Box>

            {/* КОРПОРАТИВНЫЙ EMAIL */}
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.3 }}>
                КОРПОРАТИВНЫЙ EMAIL
              </Typography>
              {mailAddress ? (
                <Box
                  component="a"
                  href={`mailto:${mailAddress}`}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.6,
                    color: ZERDE_PRIMARY,
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  <MailOutline sx={{ fontSize: 16 }} />
                  {mailAddress}
                </Box>
              ) : (
                <Typography variant="body1" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                  Не заполнен
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          /* РЕЖИМ РЕДАКТИРОВАНИЯ (ПОЛЯ СТРОГО С +7) */
          <Box component="form" onSubmit={handleSavePersonal} sx={{ display: 'flex', flexDirection: 'column', gap: 2.2 }}>
            <TextField
              label="ФИО сотрудника"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              size="small"
              fullWidth
              InputProps={{ sx: { bgcolor: '#f8fafc', borderRadius: '8px' } }}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Личный мобильный телефон"
                value={userphone}
                onChange={(e) => handlePhoneInputChange(e.target.value, setUserphone)}
                onFocus={(e) => handlePhoneFocus(e.target.value, setUserphone)}
                onBlur={(e) => handlePhoneBlur(e.target.value, setUserphone)}
                size="small"
                fullWidth
                placeholder="+7 000 000 00 00"
                helperText="Формат: +7 000 000 00 00"
                inputProps={{ maxLength: 16 }}
                InputProps={{ sx: { bgcolor: '#f8fafc', borderRadius: '8px' } }}
              />
              <TextField
                label="Рабочий телефон"
                value={workPhone}
                onChange={(e) => handlePhoneInputChange(e.target.value, setWorkPhone)}
                onFocus={(e) => handlePhoneFocus(e.target.value, setWorkPhone)}
                onBlur={(e) => handlePhoneBlur(e.target.value, setWorkPhone)}
                size="small"
                fullWidth
                placeholder="+7 000 000 00 00"
                helperText="Формат: +7 000 000 00 00"
                inputProps={{ maxLength: 16 }}
                InputProps={{ sx: { bgcolor: '#f8fafc', borderRadius: '8px' } }}
              />
            </Box>
            {canViewIIN && (
              <TextField
                label="ИИН (12 цифр)"
                value={iin}
                onChange={(e) => setIin(e.target.value.replace(/\D/g, '').slice(0, 12))}
                size="small"
                fullWidth
                InputProps={{ sx: { bgcolor: '#f8fafc', borderRadius: '8px' } }}
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Карточка 2: Дополнительная информация */}
      <Paper
        elevation={0}
        sx={{
          p: 3.5,
          bgcolor: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
        }}
      >
        <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700, fontSize: '1.1rem', mb: 2 }}>
          Дополнительные сведения
        </Typography>

        {/* Подчиненные аптеки куратора */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 1 }}>
            ПОДЧИНЕННЫЕ АПТЕКИ
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {myPharmacy ? (
              myPharmacy.split(',').map(s => s.trim()).filter(Boolean).map((pNum) => (
                <Chip
                  key={pNum}
                  label={`Аптека №${pNum}`}
                  size="small"
                  sx={{ fontWeight: 600, bgcolor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '6px' }}
                />
              ))
            ) : (
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Нет подчиненных аптек (индивидуальный доступ)
              </Typography>
            )}
          </Box>
        </Box>

        {/* Адрес аптеки */}
        <Box>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.5 }}>
            ФАКТИЧЕСКИЙ АДРЕС
          </Typography>
          <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600 }}>
            {currentAddress || (loadingGeo ? 'Загрузка адреса...' : 'Адрес ещё не зафиксирован')}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserProfileSection;
